// ============================================================================
// IDEATION SERVICE - Frontend API Client
// Handles API calls for ideation campaigns and submissions
// ============================================================================

import { api } from './api';

// ============================================================================
// Types
// ============================================================================

export interface Campaign {
  campaignId: string;
  projectId: string;
  name: string;
  description: string;
  status: 'active' | 'closed';
  formUrl: string;
  createdAt: string;
  createdBy: string;
  submissionCount: number;
}

export type PriorityLabel = 'Quick Win' | 'Strategic Initiative' | 'Deprioritise' | 'Revisit Later';

export interface Submission {
  submissionId: string;
  projectId: string;
  campaignId: string;
  source: 'portal' | 'n8n-form';
  submittedAt: string;
  submitterName: string;
  submitterTeam: string;
  submitterEmail: string;
  answers: Record<string, any>;
  complexityScore: number;
  viabilityScore: number;
  impactScore: number;
  compositeScore: number;
  priorityLabel: PriorityLabel;
}

export interface IdeationScores {
  complexityScore: number;
  viabilityScore: number;
  impactScore: number;
  compositeScore: number;
  priorityLabel: PriorityLabel;
}

export interface CampaignStats {
  total: number;
  byPriority: Record<string, number>;
  avgScores: {
    complexity: number;
    viability: number;
    impact: number;
    composite: number;
  };
}

// ============================================================================
// Service
// ============================================================================

export const ideationService = {
  // ---- Campaigns ----

  async createCampaign(
    projectId: string,
    input: { name: string; description?: string; formUrl?: string }
  ): Promise<{ campaignId: string }> {
    const response = await api.post(`/api/projects/${projectId}/ideation/campaigns`, input);
    return { campaignId: response.data.campaignId };
  },

  async listCampaigns(projectId: string): Promise<Campaign[]> {
    const response = await api.get(`/api/projects/${projectId}/ideation/campaigns`);
    return response.data.campaigns;
  },

  async getCampaign(projectId: string, campaignId: string): Promise<Campaign> {
    const response = await api.get(`/api/projects/${projectId}/ideation/campaigns/${campaignId}`);
    return response.data.campaign;
  },

  async updateCampaign(
    projectId: string,
    campaignId: string,
    updates: { status?: 'active' | 'closed'; name?: string; description?: string }
  ): Promise<void> {
    await api.patch(`/api/projects/${projectId}/ideation/campaigns/${campaignId}`, updates);
  },

  async deleteCampaign(projectId: string, campaignId: string): Promise<void> {
    await api.delete(`/api/projects/${projectId}/ideation/campaigns/${campaignId}`);
  },

  // ---- Submissions ----

  async listSubmissions(projectId: string, campaignId?: string): Promise<Submission[]> {
    const params = campaignId ? `?campaignId=${campaignId}` : '';
    const response = await api.get(`/api/projects/${projectId}/ideation/submissions${params}`);
    return response.data.submissions;
  },

  async createSubmission(
    projectId: string,
    data: {
      campaignId: string;
      answers: Record<string, any>;
      submitterName?: string;
      submitterTeam?: string;
      submitterEmail?: string;
    }
  ): Promise<{ submissionId: string; scores: IdeationScores }> {
    const response = await api.post(`/api/projects/${projectId}/ideation/submissions`, data);
    return { submissionId: response.data.submissionId, scores: response.data.scores };
  },

  async deleteSubmission(projectId: string, submissionId: string): Promise<void> {
    await api.delete(`/api/projects/${projectId}/ideation/submissions/${submissionId}`);
  },

  // ---- Stats ----

  async getStats(projectId: string, campaignId?: string): Promise<CampaignStats> {
    const params = campaignId ? `?campaignId=${campaignId}` : '';
    const response = await api.get(`/api/projects/${projectId}/ideation/stats${params}`);
    return response.data.stats;
  },

  // ---- Import / Export ----

  async importSubmissions(
    projectId: string,
    campaignId: string,
    file: File
  ): Promise<{ imported: number; failed: number; total: number; errors?: { row: number; error: string }[]; unmappedColumns?: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('campaignId', campaignId);
    const response = await api.post(`/api/projects/${projectId}/ideation/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  async exportSubmissions(projectId: string, campaignId?: string): Promise<void> {
    const params = campaignId ? `?campaignId=${campaignId}` : '';
    const response = await api.get(`/api/projects/${projectId}/ideation/export${params}`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', campaignId ? `ideation-${campaignId}.xlsx` : `ideation-${projectId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async downloadTemplate(projectId: string): Promise<void> {
    const response = await api.get(`/api/projects/${projectId}/ideation/import-template`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'ideation-import-template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};
